import React, { useContext, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
// import * as AppPropTypes from '../lib/PropTypes';
import { useSocket } from '@folklore/socket';

import { useUser } from './AuthContext';
import { useOrganisation } from './OrganisationContext';

const DataCacheContext = React.createContext(null);

export const useDataCache = () => useContext(DataCacheContext);

export const useEntityCache = (type, id) => {
    const { findEntity, updateEntity } = useDataCache();
    const entity = useMemo(() => findEntity(type, id), [type, id, findEntity]);
    const setEntity = useCallback((newData) => updateEntity(type, id, newData), [type, id]);
    return [entity, setEntity];
};

const propTypes = {
    children: PropTypes.node.isRequired,
    data: PropTypes.objectOf(PropTypes.objectOf(PropTypes.any)),
};

const defaultProps = {
    data: null,
};

export const DataCacheProvider = ({ data: initialData, children }) => {
    const [data, setData] = useState(initialData);
    const user = useUser();
    const organisation = useOrganisation();
    const userId = user !== null ? user.id : null;
    const organisationId = organisation !== null ? organisation.id : null;

    const updateEntity = useCallback(
        (type, id, entityData) => {
            if (type === null || id === null) {
                return;
            }
            const newData = {
                ...data,
                [type]: {
                    ...(data !== null ? data[type] || null : null),
                    [id]: entityData,
                },
            };
            setData(newData);
        },
        [data, setData],
    );

    const findEntity = useCallback(
        (type, id) =>
            data !== null && type !== null && id !== null ? (data[type] || {})[id] || null : null,
        [data],
    );

    const entityKeys = useMemo(() => (data !== null ? Object.keys(data) : null), [data]);
    const onSocketMessage = useCallback(
        ({ event = null, data: newEntity }) => {
            if (entityKeys === null) {
                return;
            }
            const regExp = new RegExp(`^(${entityKeys.join('|')})\\.updated$`);
            const matches = regExp.exec(event);
            if (matches !== null) {
                updateEntity(matches[1], newEntity.id, newEntity);
            }
        },
        [entityKeys, updateEntity],
    );
    const channels = useMemo(() => {
        const newChannels = [];
        if (userId !== null) {
            newChannels.push(`users.${userId}`);
        }
        if (organisationId !== null) {
            newChannels.push(`organisations.${organisationId}`);
        }
        return newChannels;
    }, [userId, organisationId]);

    useSocket(channels, {
        onMessage: onSocketMessage,
    });

    const value = useMemo(
        () => ({ data, setData, updateEntity, findEntity }),
        [data, setData, updateEntity, findEntity],
    );

    return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
};

DataCacheProvider.propTypes = propTypes;
DataCacheProvider.defaultProps = defaultProps;

export default DataCacheContext;
